"use client";

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// =========================================================

export interface VariantProps {
  title?: string;
  subtitle?: string;
  logos?: Array<{
    id: string;
    imageUrl: string;
    alt: string;
    companyName?: string;
  }>;
  animationDuration?: string;
  pauseOnHover?: boolean;
  showTitle?: boolean;
}

const defaultLogos = [
  { id: "1", imageUrl: "/partner-1.png", alt: "Partner 1", companyName: "Partner 1" },
  { id: "2", imageUrl: "/partner-2.png", alt: "Partner 2", companyName: "Partner 2" },
  { id: "3", imageUrl: "/partner-3.png", alt: "Partner 3", companyName: "Partner 3" },
  { id: "4", imageUrl: "/partner-4.png", alt: "Partner 4", companyName: "Partner 4" },
  { id: "5", imageUrl: "/partner-5.png", alt: "Partner 5", companyName: "Partner 5" },
  { id: "6", imageUrl: "/partner-6.png", alt: "Partner 6", companyName: "Partner 6" },
  { id: "7", imageUrl: "/partner-7.png", alt: "Partner 7", companyName: "Partner 7" },
  { id: "8", imageUrl: "/partner-8.png", alt: "Partner 8", companyName: "Partner 8" },
];

const BrandingShowcase: React.FC<VariantProps> = ({
  title = "Đối tác và khách hàng của chúng tôi",
  subtitle = "Hơn 50 đối tác và 1,000+ khách hàng tin dùng",
  logos = defaultLogos,
  animationDuration = "12s",
  pauseOnHover = true,
  showTitle = true,
}) => {
  // Duplicate logos for seamless infinite scroll
  const duplicatedLogos = [...logos, ...logos, ...logos];

  // Responsive logo width - more logos visible on larger screens
  const logoWidth = {
    mobile: "calc(100% / 3)", // 3 logos visible on mobile
    tablet: "calc(100% / 4)", // 4 logos visible on tablet
    laptop: "calc(100% / 6)", // 6 logos visible on laptop+
  };

  const containerStyles = {
    overflow: "hidden",
    position: "relative" as const,
    width: "100%",
    backgroundColor: "#f8f9fa",
    py: { xs: 4, md: 6 },
  };

  const scrollerStyles = {
    display: "flex",
    // width: `${(duplicatedLogos.length * 100) / visibleLogos}%`,
    animation: `scroll ${animationDuration} linear infinite`,
    "&:hover": pauseOnHover ? { animationPlayState: "paused" } : {},
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  };

  const logoStyles = {
    flex: {
      mobile: `0 0 ${logoWidth.mobile}`,
      tablet: `0 0 ${logoWidth.tablet}`,
      laptop: `0 0 ${logoWidth.laptop}`,
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    px: { mobile: 0.5, tablet: 0.75, laptop: 1 }, // Responsive padding
  };

  const cardStyles = {
    width: { mobile: "100px", tablet: "120px", laptop: "140px" }, // Responsive card width
    height: { mobile: "60px", tablet: "70px", laptop: "80px" }, // Responsive card height
    backgroundColor: "white",
    border: "1px solid #e0e0e0",
    borderRadius: { mobile: "8px", tablet: "10px", laptop: "12px" }, // Responsive border radius
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  };

  const imageStyles = {
    width: "100%",
    height: "100%",
    objectFit: "contain" as const,
    borderRadius: { mobile: "8px", tablet: "10px", laptop: "12px" }, // Responsive border radius
  };

  const keyframes = `
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(calc(-100% / 3));
      }
    }
  `;

  return (
    <Box sx={containerStyles}>
      <style>{keyframes}</style>

      {showTitle && (
        <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
          <Typography
            variant="body1"
            component="p"
            sx={{
              fontWeight: 400,
              mb: 1,
              color: "#FFA726", // Orange/yellow color for title
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="h3"
            component="h2"
            sx={{
              color: "#12384D", // Brand black color for subtitle
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              fontWeight: 700,
              letterSpacing: "0.02em",
              lineHeight: 1.2,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      )}

      <Box sx={scrollerStyles} role="img" aria-label="Partner and client logos showcase">
        {duplicatedLogos.map((logo, index) => (
          <Box key={`${logo.id}-${index}`} sx={logoStyles}>
            <Box sx={cardStyles}>
              <Box
                component="img"
                src={logo.imageUrl}
                alt={logo.alt}
                title={logo.companyName}
                sx={imageStyles}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BrandingShowcase;
